import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Leaf, Brain, Handshake, LifeBuoy, Stethoscope, Lock, LineChart, Mail } from 'lucide-react';

export default function OmStillerom() {
    return (
        <div className="min-h-screen bg-bone text-ink" data-testid="om-page">
            <header className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
                <Link to="/" className="flex items-center gap-2 text-sm text-inkSoft hover:text-ink" data-testid="om-back-link">
                    <ArrowLeft className="w-4 h-4"/> Tilbake
                </Link>
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-sage flex items-center justify-center">
                        <Leaf className="w-3.5 h-3.5 text-bone" />
                    </div>
                    <span className="font-display font-bold tracking-tight">Stillerom</span>
                </div>
            </header>

            <main className="max-w-3xl mx-auto px-6 pb-24">
                <div className="flex items-center gap-3 mt-6">
                    <div className="w-10 h-10 rounded-full bg-sage-soft flex items-center justify-center text-sage-dark">
                        <Brain className="w-5 h-5"/>
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Om Stillerom</h1>
                </div>

                <p className="mt-6 text-ink leading-relaxed">
                    Stillerom er en lavterskel samtaleplattform for deg som trenger å lufte tanker, følelser eller bekymringer – uten å måtte ta store steg.
                </p>
                <p className="mt-3 text-inkSoft leading-relaxed">
                    Vi er laget for øyeblikkene før man eventuelt oppsøker hjelp.
                </p>

                <section className="mt-12" data-testid="om-role-section">
                    <div className="flex items-center gap-2">
                        <Handshake className="w-5 h-5 text-sage-dark"/>
                        <h2 className="font-display text-xl font-semibold">Vår rolle</h2>
                    </div>

                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <div className="rounded-2xl border border-sage/30 bg-sage/5 p-5">
                            <p className="text-sm font-medium text-sage-dark mb-2">Stillerom er:</p>
                            <ul className="text-sm text-ink space-y-1.5">
                                <li>• Et sted å snakke</li>
                                <li>• Et sted å reflektere</li>
                                <li>• Et første steg</li>
                            </ul>
                        </div>
                        <div className="rounded-2xl border border-sand bg-white p-5">
                            <p className="text-sm font-medium text-inkSoft mb-2">Stillerom er ikke:</p>
                            <ul className="text-sm text-ink space-y-1.5">
                                <li>• En helsetjeneste</li>
                                <li>• Diagnostisering eller behandling</li>
                                <li>• En erstatning for profesjonell hjelp</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section className="mt-12" data-testid="om-crisis-section">
                    <div className="flex items-center gap-2">
                        <LifeBuoy className="w-5 h-5 text-crisis"/>
                        <h2 className="font-display text-xl font-semibold">Når bør du søke hjelp</h2>
                    </div>
                    <p className="mt-3 text-ink">Hvis du opplever akutte eller alvorlige utfordringer:</p>
                    <div className="mt-4 rounded-2xl border border-crisis/30 bg-crisis/5 p-5 space-y-2 text-sm">
                        <p><span className="font-medium text-ink">Legevakt:</span> <a href="tel:116117" className="text-crisis font-semibold" data-testid="om-legevakt-link">116 117</a></p>
                        <p><span className="font-medium text-ink">Akutt fare:</span> <a href="tel:113" className="text-crisis font-semibold" data-testid="om-113-link">113</a></p>
                        <p className="text-inkSoft">Eller ta kontakt med en profesjonell helsetjeneste.</p>
                    </div>
                </section>

                <section className="mt-12">
                    <div className="flex items-center gap-2">
                        <Stethoscope className="w-5 h-5 text-sage-dark"/>
                        <h2 className="font-display text-xl font-semibold">Faglig forankring</h2>
                    </div>
                    <p className="mt-3 text-ink">Stillerom utvikles med fokus på:</p>
                    <ul className="mt-2 text-ink space-y-1.5">
                        <li>• Etiske retningslinjer</li>
                        <li>• Trygg bruk</li>
                        <li>• Ansvarlig kommunikasjon</li>
                    </ul>
                    <p className="mt-4 text-sm italic text-inkSoft">"Testet i pilot med 50 brukere – 8 av 10 sier de fikk satt ord på noe de slet med å snakke om."</p>
                </section>

                <section className="mt-12">
                    <div className="flex items-center gap-2">
                        <Lock className="w-5 h-5 text-sage-dark"/>
                        <h2 className="font-display text-xl font-semibold">Personvern og trygghet</h2>
                    </div>
                    <p className="mt-3 text-ink">Vi tar personvern på alvor.</p>
                    <ul className="mt-2 text-ink space-y-1.5">
                        <li>• Du velger selv hva du deler</li>
                        <li>• Data brukes kun til å levere tjenesten</li>
                        <li>• Ingen salg av persondata</li>
                    </ul>
                    <p className="mt-3 text-sm">
                        <Link to="/personvern" className="text-sage-dark underline font-medium" data-testid="om-privacy-link">
                            Les personvernerklæringen
                        </Link>
                    </p>
                </section>

                <section className="mt-12">
                    <div className="flex items-center gap-2">
                        <LineChart className="w-5 h-5 text-sage-dark"/>
                        <h2 className="font-display text-xl font-semibold">Utvikling og testing</h2>
                    </div>
                    <p className="mt-3 text-ink">Stillerom er under kontinuerlig utvikling.</p>
                    <p className="mt-2 text-ink">Vi tester løsningen i små piloter for å sikre at den:</p>
                    <ul className="mt-2 text-ink space-y-1.5">
                        <li>• Oppleves trygg</li>
                        <li>• Faktisk hjelper</li>
                        <li>• Ikke gjør skade</li>
                    </ul>
                </section>

                <section className="mt-12 mb-16">
                    <div className="flex items-center gap-2">
                        <Mail className="w-5 h-5 text-sage-dark"/>
                        <h2 className="font-display text-xl font-semibold">Kontakt oss</h2>
                    </div>
                    <p className="mt-3 text-ink">
                        Har du spørsmål eller bekymringer?{' '}
                        <a
                            href="mailto:info@stillerom.net"
                            className="text-sage-dark underline font-medium"
                            data-testid="om-contact-email"
                        >
                            info@stillerom.net
                        </a>
                    </p>
                </section>
            </main>

            <footer className="border-t border-sand">
                <div className="max-w-3xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-3 text-xs text-inkSoft">
                    <p>© {new Date().getFullYear()} Stillerom</p>
                    <div className="flex items-center gap-4">
                        <Link to="/personvern" className="hover:text-ink" data-testid="om-footer-privacy">Personvern</Link>
                        <Link to="/vilkar" className="hover:text-ink" data-testid="om-footer-terms">Vilkår</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
}
